/*
================================================================================
| FILENAME: src/features/datamanagement/DataManagementView.jsx (Updated)
| PURPOSE:  To refactor the Data Hub UI and re-introduce the WordPress
|           category table creation functionality.
|
| KEY CHANGES:
| 1.  Fixed a syntax error in the fetchAllData function's try...catch block.
| 2.  Updated colors and styles to align with the FoundryDashboardView for
|     visual consistency (e.g., headers, backgrounds, borders).
| 3.  Restored "Analyze Content" and "Set as CTA Source" functionality.
| 4.  Added Analyze (Wand) and CTA Source (Star) buttons to the TableCard.
================================================================================
*/
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Database, RefreshCw, Trash2, Wand2, Plus, Clock, AlertTriangle, FileText, BarChart3, Megaphone, Search, Code, Users, UploadCloud, X, Filter, Loader, FileUp, Star, Sparkles } from 'lucide-react'; 

import apiService from '../../services/apiService'; // Assuming this service exists and works
import { useAuth } from '../../hooks/useAuth'; // Assuming this hook exists
import Modal from '../../components/Modal'; // Assuming a generic Modal component exists

// --- Helper Functions & Constants ---
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
};

const SOURCE_ICONS = {
    googleAnalytics: <BarChart3 className="w-5 h-5" />,
    googleAds: <Megaphone className="w-5 h-5" />,
    searchConsole: <Search className="w-5 h-5" />,
    wordpress: <Code className="w-5 h-5" />,
    hubspot: <Users className="w-5 h-5" />,
    offline: <UploadCloud className="w-5 h-5" />,
    asset_summary: <Sparkles className="w-5 h-5" />, 
    default: <Database className="w-5 h-5" />,
    offline: <UploadCloud className="w-5 h-5" />, // Added icon for offline
    default: <Database className="w-5 h-5" />,
};

const CONNECTION_CONFIG = [
    { id: 'googleAnalytics', name: 'Google Analytics' },
    { id: 'googleAds', name: 'Google Ads' },
    { id: 'searchConsole', name: 'Google Search Console' },
    { id: 'wordpress', name: 'WordPress' },
    { id: 'hubspot', name: 'HubSpot' },
    { id: 'asset_summary', name: 'Asset Summaries' },
    { id: 'offline', name: 'Manual Uploads' }, // Added config for offline
];

// --- Reusable UI Sub-components ---

const DataSourceSidebar = ({ sources, selectedSource, onSelectSource }) => (
    <aside className="w-full md:w-64 lg:w-72 flex-shrink-0 bg-slate-800/50 p-4 rounded-lg border border-slate-700">
        <h2 className="text-lg font-semibold text-white mb-4">Data Sources</h2>
        <nav className="space-y-2">
            {sources.map(source => (
                <button
                    key={source.id}
                    onClick={() => onSelectSource(source.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                        selectedSource === source.id
                            ? 'bg-slate-700 text-white'
                            : 'text-slate-400 hover:bg-slate-700/60 hover:text-slate-200'
                    }`}
                >
                    {SOURCE_ICONS[source.id] || SOURCE_ICONS.default}
                    <span className="flex-grow">{source.name}</span>
                </button>
            ))}
        </nav>
    </aside>
);

const TableCard = ({ table, onRefresh, onDelete, onView, onAnalyze, onSetCtaSource }) => (
    <div className={`bg-slate-800/50 border rounded-lg shadow-lg transition-all hover:border-teal-500/50 hover:shadow-teal-500/10 flex flex-col ${table.isCtaSource ? 'border-yellow-400' : 'border-slate-700'}`}>
        <div className="p-4 cursor-pointer flex-grow" onClick={() => onView(table._id)}>
            <div className="flex items-start justify-between">
                <h3 className="font-bold text-white truncate pr-2">{table.name}</h3>
                {table.isCtaSource && <Star size={16} className="text-yellow-400 fill-current flex-shrink-0" title="This is the CTA Recommendation Source"/>}
            </div>
            <div className="text-sm text-slate-400 mt-2 space-y-1">
                <p>Rows: {table.rows?.length || table.rowCount || 0}</p>
                <p>Last Refreshed: {formatDate(table.lastRefreshed).split(',')[0]}</p>
            </div>
        </div>
        <div className="border-t border-slate-700 p-2 flex items-center justify-end gap-1">
            {table.source === 'wordpress' && (
                <>
                    <button onClick={(e) => { e.stopPropagation(); onAnalyze(table._id); }} className="p-2 rounded-lg text-slate-400 hover:bg-purple-500/50 hover:text-purple-300 disabled:opacity-50" disabled={table.status === 'processing'} title="Analyze with AI">
                        <Wand2 size={16} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onSetCtaSource(table._id); }} className={`p-2 rounded-lg disabled:opacity-50 ${table.isCtaSource ? 'text-yellow-400 bg-yellow-500/20' : 'text-slate-400 hover:bg-yellow-500/50 hover:text-yellow-300'}`} title="Set as CTA Source">
                        <Star size={16} className={table.isCtaSource ? 'fill-current' : ''}/>
                    </button>
                </>
            )}
            <button 
                onClick={(e) => { e.stopPropagation(); onRefresh(table._id, table.source); }} 
                className="p-2 rounded-lg text-slate-400 hover:bg-teal-500/50 hover:text-teal-300 disabled:opacity-50" 
                disabled={table.status === 'processing'} 
                title={table.source === 'offline' ? "Overwrite with new file" : "Refresh Data"}
            >
                {table.status === 'processing' ? <Loader size={16} className="animate-spin" /> : (table.source === 'offline' ? <FileUp size={16} /> : <RefreshCw size={16} />)}
            </button>
            {!table.isPrebuilt && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(table._id); }} className="p-2 rounded-lg text-slate-400 hover:bg-red-500/50 hover:text-red-400" title="Delete Table">
                    <Trash2 size={16} />
                </button>
            )}
        </div>
    </div>
);

const DataTable = ({ table }) => {
    const [filter, setFilter] = useState('');
    const hasData = table.rows && Array.isArray(table.rows) && table.rows.length > 0;

    const filteredData = useMemo(() => {
        if (!hasData) return [];
        return table.rows.filter(row =>
            row.some(cell => String(cell).toLowerCase().includes(filter.toLowerCase()))
        );
    }, [table.rows, filter, hasData]);
    
    return (
        <div className="flex flex-col bg-slate-900 rounded-lg overflow-hidden max-h-[80vh]">
            <div className="p-4 border-b border-slate-700 flex-shrink-0">
                 <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h3 className="font-bold text-white text-lg">{table.name}</h3>
                        <p className="text-slate-400 text-sm">{table.rows?.length || 0} rows</p>
                    </div>
                    {hasData && (
                        <div className="relative flex-grow max-w-xs w-full">
                            <Filter size={16} className="text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input type="text" placeholder="Filter data..." value={filter} onChange={e => setFilter(e.target.value)} className="w-full bg-slate-700/50 text-white rounded-lg py-2 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                        </div>
                    )}
                 </div>
            </div>
            {table.status === 'error' && <div className="p-4 bg-red-900/30 text-red-300 text-sm">{table.errorMessage}</div>}
            {hasData ? (
                <div className="overflow-auto">
                    <table className="min-w-full text-sm text-left">
                        <thead className="sticky top-0 bg-slate-800 z-10">
                            <tr>{table.headers.map(header => <th key={header} className="p-3 font-semibold text-slate-300 uppercase tracking-wider">{header}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                            {filteredData.map((row, index) => (
                                <tr key={index} className="hover:bg-slate-800/50">
                                    {row.map((cell, cellIndex) => <td key={cellIndex} className="p-3 whitespace-nowrap text-slate-300">{String(cell)}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                 <div className="flex items-center justify-center h-48 text-slate-500">
                    <p>No data to display or table is currently processing.</p>
                </div>
            )}
        </div>
    );
};


// --- Modals ---

const NewTableModal = ({ isOpen, onClose, onSubmit, sources }) => {
    const [name, setName] = useState('');
    const [aiQuery, setAiQuery] = useState('');
    const [source, setSource] = useState(sources.length > 0 ? sources[0].id : '');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if(isOpen && sources.length > 0 && !source) {
            setSource(sources[0].id);
        }
    }, [isOpen, sources, source]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !source) return;
        setIsLoading(true);
        try {
            await onSubmit(name.trim(), source, aiQuery.trim());
            setName('');
            setAiQuery('');
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
            onClose();
        }
    };
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Create New Data Table with AI">
            <form onSubmit={handleSubmit} className="space-y-4">
                 <div>
                    <label htmlFor="source" className="block mb-2 text-sm font-medium text-slate-300">Data Source</label>
                    <select id="source" value={source} onChange={e => setSource(e.target.value)} className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5">
                        {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="tableName" className="block mb-2 text-sm font-medium text-slate-300">Table Name</label>
                    <input type="text" id="tableName" value={name} onChange={e => setName(e.target.value)} required className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5" placeholder="e.g., Top Converting Pages" />
                </div>
                <div>
                    <label htmlFor="aiQuery" className="flex items-center gap-2 mb-2 text-sm font-medium text-slate-300">
                        <Wand2 className="w-4 h-4 text-teal-400" /> AI Prompt (Optional)
                    </label>
                    <textarea id="aiQuery" value={aiQuery} onChange={e => setAiQuery(e.target.value)} className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5" rows="3" placeholder="e.g., 'Top 10 countries by users in the last 60 days'"></textarea>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-slate-600 text-white font-semibold hover:bg-slate-500">Cancel</button>
                    <button type="submit" disabled={isLoading} className="px-4 py-2 rounded-md bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:bg-teal-800 flex items-center justify-center gap-2">
                        {isLoading && <Loader className="w-5 h-5 animate-spin"/>}
                        {isLoading ? 'Creating...' : 'Create Table'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

const WpCategoryTableModal = ({ isOpen, onClose, onSubmit }) => {
    const [tableName, setTableName] = useState('');
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            setError('');
            setSelectedCategories([]);
            setTableName('');
            apiService.fetchWpCategories()
                .then(data => setCategories(data || []))
                .catch(() => setError('Could not load categories.'))
                .finally(() => setIsLoading(false));
        }
    }, [isOpen]);

    const handleCategoryToggle = (catId) => {
        setSelectedCategories(prev =>
            prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (tableName.trim() && selectedCategories.length > 0) {
            await onSubmit(tableName.trim(), selectedCategories);
            onClose();
        }
    };

    const parentCategories = useMemo(() => categories.filter(c => c.parent === 0), [categories]);
    const getChildCategories = useCallback((parentId) => categories.filter(c => c.parent === parentId), [categories]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Create Table from WordPress Categories">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="tableNameCat" className="block mb-2 text-sm font-medium text-slate-300">New Table Name</label>
                    <input type="text" id="tableNameCat" value={tableName} onChange={e => setTableName(e.target.value)} required className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5" placeholder="e.g., Gated Content Assets" />
                </div>
                <div>
                    <label className="block mb-2 text-sm font-medium text-slate-300">Select Categories</label>
                    {isLoading && <div className="flex justify-center p-4"><Loader className="animate-spin text-teal-400" /></div>}
                    {error && <p className="text-red-400 p-4 bg-red-900/30 rounded-lg">{error}</p>}
                    {!isLoading && !error && (
                        <div className="max-h-60 overflow-y-auto space-y-2 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                            {parentCategories.length > 0 ? parentCategories.map(parentCat => (
                                <div key={parentCat.id}>
                                    <div className="flex items-center">
                                        <input type="checkbox" id={`cat-${parentCat.id}`} checked={selectedCategories.includes(parentCat.id)} onChange={() => handleCategoryToggle(parentCat.id)} className="h-4 w-4 rounded bg-slate-700 border-slate-600 text-teal-600 focus:ring-teal-500" />
                                        <label htmlFor={`cat-${parentCat.id}`} className="ml-2 font-semibold text-slate-200">{parentCat.name} ({parentCat.count})</label>
                                    </div>
                                    <div className="ml-6 mt-1 space-y-1">
                                        {getChildCategories(parentCat.id).map(childCat => (
                                             <div key={childCat.id} className="flex items-center">
                                                <input type="checkbox" id={`cat-${childCat.id}`} checked={selectedCategories.includes(childCat.id)} onChange={() => handleCategoryToggle(childCat.id)} className="h-4 w-4 rounded bg-slate-700 border-slate-600 text-teal-600 focus:ring-teal-500" />
                                                <label htmlFor={`cat-${childCat.id}`} className="ml-2 text-slate-300">{childCat.name} ({childCat.count})</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )) : <p className="text-slate-500 text-center py-4">No categories found.</p>}
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-3 pt-4">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-slate-600 text-white font-semibold hover:bg-slate-500">Cancel</button>
                    <button type="submit" disabled={!tableName.trim() || selectedCategories.length === 0} className="px-4 py-2 rounded-md bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:bg-teal-800">Create Table</button>
                </div>
            </form>
        </Modal>
    );
};


const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
    if (!isOpen) return null;
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="">
             <div className="text-center p-4">
                <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
                <h3 className="mt-2 text-lg font-medium text-white">{title}</h3>
                <p className="mt-2 text-sm text-slate-400">{message}</p>
            </div>
            <div className="mt-4 flex justify-center gap-4 p-4 bg-slate-900/50 rounded-b-lg">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-slate-600 text-white font-semibold hover:bg-slate-500 w-full">Cancel</button>
                <button onClick={onConfirm} className="px-4 py-2 rounded-md bg-red-600 text-white font-semibold hover:bg-red-700 w-full">Confirm</button>
            </div>
        </Modal>
    );
};
// --- NEW MODAL for File Uploads ---
const UploadFileModal = ({ isOpen, onClose, onSubmit, mode = 'create', tableId }) => {
    const [tableName, setTableName] = useState('');
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            setTableName('');
            setFile(null);
            setError('');
            setIsLoading(false);
        }
    }, [isOpen]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const allowedTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
            if (!allowedTypes.includes(selectedFile.type)) {
                setError('Invalid file type. Please upload a CSV or XLSX file.');
                setFile(null);
            } else {
                setError('');
                setFile(selectedFile);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file || (mode === 'create' && !tableName.trim())) {
            setError('Please provide all required fields.');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            await onSubmit({ tableName, file, tableId });
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An unknown error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={mode === 'create' ? "Create Table from File" : "Overwrite Table with File"}>
            <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'create' && (
                    <div>
                        <label htmlFor="tableNameUpload" className="block mb-2 text-sm font-medium text-slate-300">Table Name</label>
                        <input type="text" id="tableNameUpload" value={tableName} onChange={e => setTableName(e.target.value)} required className="bg-slate-700 ... w-full" placeholder="e.g., Q1 Customer List" />
                    </div>
                )}
                <div>
                    <label className="block mb-2 text-sm font-medium text-slate-300">Upload File (CSV or XLSX)</label>
                    <div onClick={() => fileInputRef.current?.click()} className="flex justify-center items-center w-full px-6 py-10 border-2 border-slate-600 border-dashed rounded-lg cursor-pointer hover:border-teal-500 hover:bg-slate-800/50">
                        <div className="text-center">
                            <UploadCloud className="mx-auto h-12 w-12 text-slate-400" />
                            <p className="mt-2 text-sm text-slate-300">{file ? file.name : 'Click to select a file'}</p>
                            <p className="text-xs text-slate-500">{file ? `${(file.size / 1024).toFixed(2)} KB` : 'CSV or XLSX'}</p>
                        </div>
                        <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv, .xlsx" className="hidden" />
                    </div>
                </div>
                {error && <p className="text-red-400 text-sm">{error}</p>}
                <div className="flex justify-end gap-3 pt-4">
                    <button type="button" onClick={onClose} className="px-4 py-2 ...">Cancel</button>
                    <button type="submit" disabled={isLoading || !file} className="px-4 py-2 ...">
                        {isLoading ? 'Uploading...' : (mode === 'create' ? 'Create Table' : 'Overwrite Table')}
                    </button>
                </div>
            </form>
        </Modal>
    );
};
// --- Main View Component ---
export default function DataManagementView() {
    const { user } = useAuth();
    const [tables, setTables] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSource, setSelectedSource] = useState(CONNECTION_CONFIG[0].id);
    
    // Modal States
    const [isNewTableModalOpen, setIsNewTableModalOpen] = useState(false);
    const [isWpCategoryModalOpen, setIsWpCategoryModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [viewingTableId, setViewingTableId] = useState(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadModalConfig, setUploadModalConfig] = useState({ mode: 'create', tableId: null });


    const fetchAllData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const fetchedTables = await apiService.fetchDataTables();
            setTables(fetchedTables);
        } catch (err) {
            setError('Failed to load Data Hub. Please try again later.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { if (user) fetchAllData(); }, [user, fetchAllData]);
    
    const handleRefresh = async (tableId, source) => {
        if (source === 'offline') {
            setUploadModalConfig({ mode: 'overwrite', tableId });
            setIsUploadModalOpen(true);
        } else {
            // Existing refresh logic for API-based tables
            await handleTableAction('refresh', { tableId });
        }
    };
    
    const handleUploadSubmit = async ({ tableName, file, tableId }) => {
        setError(null);
        try {
            if (uploadModalConfig.mode === 'create') {
                await apiService.createDataTableFromFile(tableName, file);
            } else {
                await apiService.overwriteDataTable(tableId, file);
            }
            await fetchAllData();
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Upload failed.';
            setError(errorMessage);
            throw err; // re-throw to keep modal open on error
        }
    };


    const handleTableAction = async (action, payload) => {
        setError(null);
        try {
            switch (action) {
                case 'create':
                    await apiService.createDataTable(payload.name, payload.source, payload.aiQuery);
                    await fetchAllData();
                    break;
                case 'create-from-category':
                    await apiService.createWpCategoryTable(payload.tableName, payload.categoryIds);
                    await fetchAllData();
                    break;
                case 'delete':
                     setConfirmAction({
                        fn: async () => {
                            await apiService.deleteDataTable(payload.tableId);
                            await fetchAllData();
                        },
                        title: "Delete Table?",
                        message: "Are you sure you want to delete this table? This action cannot be undone."
                     });
                    setIsConfirmOpen(true);
                    return;
                case 'refresh':
                    setTables(prev => prev.map(t => t._id === payload.tableId ? { ...t, status: 'processing' } : t));
                    const refreshedTable = await apiService.fetchDataTableData(payload.tableId);
                    setTables(prev => prev.map(t => t._id === payload.tableId ? { ...refreshedTable, status: 'completed' } : t));
                    break;
                case 'analyze':
                    setTables(prev => prev.map(t => t._id === payload.tableId ? { ...t, status: 'processing' } : t));
                    const analyzedTable = await apiService.analyzeTableContent(payload.tableId);
                    setTables(prev => prev.map(t => t._id === payload.tableId ? analyzedTable : t));
                    break;
                case 'set-cta-source':
                    await apiService.setCtaSource(payload.tableId);
                    await fetchAllData();
                    break;
                default:
                    return;
            }
        } catch (err) {
            const errorMessage = err.message || `Failed to perform action: ${action}`;
            setError(errorMessage);
            if (action === 'refresh' || action === 'analyze') {
                 setTables(prev => prev.map(t => t._id === payload.tableId ? { ...t, status: 'error', errorMessage } : t));
            }
        }
    };
    
    const filteredTables = useMemo(() => tables.filter(table => table.source === selectedSource), [tables, selectedSource]);
    const viewingTable = useMemo(() => tables.find(t => t._id === viewingTableId), [tables, viewingTableId]);

    if (isLoading && !tables.length) {
        return <div className="flex justify-center items-center h-screen"><Loader className="w-10 h-10 animate-spin text-teal-500" /></div>;
    }

    return (
        <>
            <ConfirmationModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={async () => {
                    if (confirmAction?.fn) await confirmAction.fn();
                    setIsConfirmOpen(false);
                }}
                title={confirmAction?.title || "Are you sure?"}
                message={confirmAction?.message || "This action cannot be undone."}
            />
            
            <Modal isOpen={!!viewingTableId} onClose={() => setViewingTableId(null)} title="" size="6xl">
                {viewingTable && <DataTable table={viewingTable} />}
            </Modal>

            <NewTableModal 
                isOpen={isNewTableModalOpen}
                onClose={() => setIsNewTableModalOpen(false)}
                onSubmit={(name, source, aiQuery) => handleTableAction('create', {name, source, aiQuery})}
                sources={CONNECTION_CONFIG}
            />
            
            <WpCategoryTableModal
                isOpen={isWpCategoryModalOpen}
                onClose={() => setIsWpCategoryModalOpen(false)}
                onSubmit={(tableName, categoryIds) => handleTableAction('create-from-category', {tableName, categoryIds})}
            />
            
            <UploadFileModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onSubmit={handleUploadSubmit}
                mode={uploadModalConfig.mode}
                tableId={uploadModalConfig.tableId}
            />


            <div className="p-6 text-slate-300 font-sans">
                <div className="flex flex-col md:flex-row gap-6">
                    
                    <DataSourceSidebar 
                        sources={CONNECTION_CONFIG}
                        selectedSource={selectedSource}
                        onSelectSource={setSelectedSource}
                    />

                    <main className="flex-1">
                        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 border-b border-slate-700 pb-4">
                            <div>
                                <h1 className="text-3xl font-bold text-white">Data Hub</h1>
                                <p className="text-slate-400 mt-1">Manage and create your data source tables.</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {selectedSource === 'wordpress' && (
                                    <button onClick={() => setIsWpCategoryModalOpen(true)} className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors shadow-md">
                                        <FileText size={16} /> Create from Categories
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        setUploadModalConfig({ mode: 'create', tableId: null });
                                        setIsUploadModalOpen(true);
                                    }}
                                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                                >
                                    <UploadCloud size={16} /> Upload from File
                                </button>

                                <button onClick={() => setIsNewTableModalOpen(true)} className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors shadow-md">
                                    <Plus size={20} /> Create with AI
                                </button>
                            </div>
                        </header>

                        {error && <div className="mb-6 p-4 bg-red-900/50 border border-red-700 text-red-300 rounded-lg">{error}</div>}
                        
                        {isLoading && <div className="flex justify-center p-10"><Loader className="w-8 h-8 animate-spin text-teal-500" /></div>}

                        {!isLoading && filteredTables.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {filteredTables.map(table => (
                                    <TableCard 
                                        key={table._id} 
                                        table={table} 
                                        onDelete={(id) => handleTableAction('delete', { tableId: id })} 
                                        onRefresh={handleRefresh}
                                        onView={setViewingTableId}
                                        onAnalyze={(id) => handleTableAction('analyze', {tableId: id})}
                                        onSetCtaSource={(id) => handleTableAction('set-cta-source', {tableId: id})}
                                    />
                                ))}
                            </div>
                        ) : (
                           !isLoading && <div className="text-center py-16 px-6 bg-slate-800/50 rounded-lg border-2 border-dashed border-slate-700">
                                <Database size={48} className="mx-auto text-slate-600" />
                                <h3 className="mt-4 text-lg font-semibold text-white">No Tables Found</h3>
                                <p className="mt-1 text-slate-400">No data tables exist for the '{CONNECTION_CONFIG.find(c=>c.id === selectedSource)?.name}' source.</p>
                                <button onClick={() => setIsNewTableModalOpen(true)} className="mt-6 flex items-center mx-auto gap-2 px-4 py-2 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors">
                                    <Plus size={16} /> Create Your First Table
                                </button>
                           </div>
                        )}
                    </main>
                </div>
            </div>
        </>
    );
}
